import React from 'react';
import { t } from '@lingui/core/macro';
import {
  Select,
  MenuItem,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import { Block } from '../classes/Block';
import {
  FontAxisConfig,
  STATIC_FONT_WEIGHT_AXIS,
  VariableFontConfig,
  clampFontAxisValue,
  getVariableFontConfig,
} from '../utils/fontHelpers';
import { FontOption } from './ReusableFontPicker';

interface FontWeightWidthControlsProps {
  fontName: string | undefined;
  weight: number | undefined;
  width: number | undefined;
  weightField: keyof Block;
  widthField: keyof Block;
  onChange: (field: keyof Block, value: number) => void;
  weightLabel: string;
  widthLabel: string;
  variableFontConfig?: VariableFontConfig;
  fontOptions?: FontOption[];
  onFontChange?: (fontName: string) => void;
}

const FontWeightWidthControls: React.FC<FontWeightWidthControlsProps> = ({
  fontName,
  weight,
  width,
  weightField,
  widthField,
  onChange,
  weightLabel,
  widthLabel,
  variableFontConfig: providedVariableFontConfig,
  fontOptions = [],
  onFontChange,
}) => {
  const variableFontConfig: VariableFontConfig | null =
    providedVariableFontConfig ?? getVariableFontConfig(fontName);
  const currentFont: FontOption | undefined = fontOptions.find((option: FontOption) => option.value === fontName);
  const familyFonts: FontOption[] = currentFont
    ? fontOptions.filter((option: FontOption) => option.familyName === currentFont.familyName)
    : [];
  const isItalic = (name: string): boolean => /italic|it(?:\.[a-z]+)?$/i.test(name);
  const isBold = (name: string): boolean => /bold/i.test(name);
  const staticBoldFont: FontOption | undefined = familyFonts.find((option: FontOption) => isBold(option.value));
  const staticRegularFont: FontOption | undefined = familyFonts.find((option: FontOption) => !isBold(option.value));
  const italicFont: FontOption | undefined = familyFonts.find((option: FontOption) => isItalic(option.value));
  const romanFont: FontOption | undefined = familyFonts.find((option: FontOption) => !isItalic(option.value));
  const hasBold: boolean = Boolean(variableFontConfig ? variableFontConfig.weightAxis.max >= 700 : staticBoldFont);
  const hasItalic: boolean = Boolean(italicFont && romanFont);
  const boldSelected: boolean = variableFontConfig ? (weight ?? 400) >= 700 : Boolean(fontName && isBold(fontName));
  const italicSelected: boolean = Boolean(fontName && isItalic(fontName));

  const fontStyleControls = (
    <ToggleButtonGroup
      size="small"
      value={boldSelected ? ['bold'] : []}
      aria-label={t`Text style`}
      sx={{
        '& .MuiToggleButton-root': { borderColor: '#d8e0e8', color: '#465b70', padding: '4px 7px' },
        '& .MuiToggleButton-root.Mui-selected': { backgroundColor: '#e1f3fb', color: '#007CB2' },
      }}
    >
      <Tooltip title={hasBold ? t`Bold` : t`This font has no bold option`} arrow>
        <span>
          <ToggleButton
            value="bold"
            disabled={!hasBold}
            aria-label={t`Bold`}
            onClick={() => {
              if (variableFontConfig) {
                onChange(weightField, boldSelected ? 400 : 700);
              } else if (onFontChange) {
                const nextFont: FontOption | undefined = boldSelected ? staticRegularFont : staticBoldFont;
                if (nextFont) {
                  onFontChange(nextFont.value);
                  onChange(weightField, boldSelected ? 400 : 700);
                }
              }
            }}
          >
            <FormatBoldIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>
    </ToggleButtonGroup>
  );

  const italicStyleControl = (
    <ToggleButtonGroup
      className="font-italic-toggle-group"
      size="small"
      value={italicSelected ? ['italic'] : []}
      aria-label={t`Text style`}
      sx={{
        '& .MuiToggleButton-root': { borderColor: '#d8e0e8', color: '#465b70', padding: '4px 7px' },
        '& .MuiToggleButton-root.Mui-selected': { backgroundColor: '#e1f3fb', color: '#007CB2' },
      }}
    >
      <Tooltip title={hasItalic ? t`Italic` : t`This font has no italic option`} arrow>
        <span>
          <ToggleButton
            value="italic"
            disabled={!hasItalic}
            aria-label={t`Italic`}
            onClick={() => {
              if (!onFontChange) return;
              const nextFont: FontOption | undefined = italicSelected ? romanFont : italicFont;
              if (nextFont) onFontChange(nextFont.value);
            }}
          >
            <FormatItalicIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>
    </ToggleButtonGroup>
  );

  if (!variableFontConfig) {
    return (
      <div className="form-group font-weight-control">
        <label>{weightLabel}</label>
        <div className="input-wrapper" style={{ gap: '8px' }}>
          {fontStyleControls}
          <Collapse
            in={boldSelected}
            orientation="horizontal"
            timeout={160}
            easing={{ enter: 'cubic-bezier(0.2, 0, 0, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
            sx={{ display: 'flex' }}
          >
            <Select
              className="dropdown-menu-select"
              value={Math.max(weight ?? 700, 700)}
              onChange={(e) =>
                onChange(
                  weightField,
                  clampFontAxisValue(e.target.value, STATIC_FONT_WEIGHT_AXIS)
                )
              }
              style={{ width: '92px', height: '34px' }}
            >
              <MenuItem value={700}>{t`Bold`}</MenuItem>
              <MenuItem value={900}>{t`Bolder`}</MenuItem>
            </Select>
          </Collapse>
          {italicStyleControl}
        </div>
      </div>
    );
  }

  const weightAxis: FontAxisConfig = variableFontConfig.weightAxis;
  const widthAxis: FontAxisConfig | undefined = variableFontConfig.widthAxis;
  const resolvedWeight: number = clampFontAxisValue(weight, weightAxis);
  const resolvedWidth: number | null = widthAxis ? clampFontAxisValue(width, widthAxis) : null;

  return (
    <>
      <div className="form-group font-weight-control">
        <label>{weightLabel}</label>
        <div className="input-wrapper" style={{ gap: '8px' }}>
          <input
            type="range"
            min={weightAxis.min}
            max={weightAxis.max}
            step={weightAxis.step}
            value={resolvedWeight}
            onChange={(e) =>
              onChange(
                weightField,
                clampFontAxisValue(e.target.value, weightAxis)
              )
            }
            style={{ flex: 1, minWidth: 0, width: 'auto' }}
          />
          <input
            type="number"
            min={weightAxis.min}
            max={weightAxis.max}
            step={weightAxis.step}
            value={resolvedWeight}
            onChange={(e) =>
              onChange(
                weightField,
                clampFontAxisValue(e.target.value, weightAxis)
              )
            }
            style={{ width: '68px', flexShrink: 0 }}
          />
          {italicStyleControl}
        </div>
      </div>
      {widthAxis && resolvedWidth !== null && (
        <div className="form-group">
          <label>{widthLabel}</label>
          <div className="input-wrapper" style={{ gap: '8px' }}>
            <input
              type="range"
              min={widthAxis.min}
              max={widthAxis.max}
              step={widthAxis.step}
              value={resolvedWidth}
              onChange={(e) =>
                onChange(
                  widthField,
                  clampFontAxisValue(e.target.value, widthAxis)
                )
              }
              style={{ flex: 1, minWidth: 0, width: 'auto' }}
            />
            <input
              type="number"
              min={widthAxis.min}
              max={widthAxis.max}
              step={widthAxis.step}
              value={resolvedWidth}
              onChange={(e) =>
                onChange(
                  widthField,
                  clampFontAxisValue(e.target.value, widthAxis)
                )
              }
              style={{ width: '68px', flexShrink: 0 }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FontWeightWidthControls;
